import React, { MouseEventHandler, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Connection, getConnectionHealthStatuses, Organization, ProviderType, DestinationType, Destination } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";
import GlobalSearch from "../common/GlobalSearch";
import { containerClasses, textClasses, buttonClasses, filterClasses, paginationClasses, skeletonClasses } from "../common/tailwindClassnames";
import { CopyToClipboard, ArrowForward } from "../common/ProjectIcons";
import SearchChip from "../common/SearchChip";
import { toast } from 'react-toastify';
import NoResults from "../common/NoResults";
import { page_size } from "../../api/apiUtils";
import { getConnectionStatus } from "./getConnectionStatus";
import { HoverTooltip } from "../common/HoverTooltip";
import SelectInput from "../common/SelectInput";

const getHostname = (url: string): string => {
    try { return new URL(url).hostname; } catch { return url; }
};

type DestWithDisplay = Destination & { displayUrl: string, hasUrl: boolean };

const DestinationCellContent = React.memo(({ destinations, t }: {
    destinations: Destination[],
    t: (key: string, opts?: any) => string,
}) => {
    const groups = useMemo(() => {
        const withDisplay: DestWithDisplay[] = destinations.map((dest) => {
            if (!dest.base_url.trim()) return { ...dest, displayUrl: dest.name, hasUrl: false };
            let displayUrl = dest.base_url.trim();
            try { displayUrl = new URL(dest.base_url).hostname; } catch {}
            return { ...dest, displayUrl, hasUrl: true };
        });
        return withDisplay.reduce((acc: Record<string, DestWithDisplay[]>, dest) => {
            if (!acc[dest.type.name]) acc[dest.type.name] = [];
            acc[dest.type.name].push(dest);
            return acc;
        }, {});
    }, [destinations]);

    const MAX_VISIBLE = 2;
    return (
        <>
            {Object.entries(groups).map(([typeName, dests]) => {
                const visible = dests.slice(0, MAX_VISIBLE);
                const hiddenCount = dests.length - visible.length;
                return (
                    <div key={typeName} className="mb-1 last:mb-0">
                        <span className="block text-base font-normal text-grey-g0">{typeName}</span>
                        {visible.map((dest) => (
                            <div key={dest.id} className="flex items-center gap-1 min-w-0">
                                <span className="text-xs text-secondary-gray truncate" title={dest.hasUrl ? dest.base_url : dest.name}>
                                    {dest.displayUrl}
                                </span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard.writeText(dest.hasUrl ? dest.base_url : dest.name);
                                        toast.info(`${t("copied to clipboard")}`, {
                                            position: toast.POSITION.TOP_RIGHT,
                                            autoClose: 2000
                                        });
                                    }}
                                    className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                                    aria-label={t("Copy to clipboard")}
                                >
                                    <CopyToClipboard />
                                </button>
                            </div>
                        ))}
                        {hiddenCount > 0 && (
                            <HoverTooltip
                                position="right"
                                align="left"
                                width="w-52"
                                content={
                                    <div className="text-xs flex flex-col gap-1">
                                        {dests.slice(MAX_VISIBLE).map((d) => (
                                            <span key={d.id} className="text-secondary-gray truncate" title={d.hasUrl ? d.base_url : d.name}>
                                                {d.displayUrl}
                                            </span>
                                        ))}
                                    </div>
                                }
                            >
                                <span
                                    className="text-xs text-accent-green font-normal leading-normal underline cursor-default"
                                    style={{ textDecorationSkipInk: 'none', textUnderlinePosition: 'from-font' }}
                                >
                                    {t("+ {{count}} other", { count: hiddenCount })}
                                </span>
                            </HoverTooltip>
                        )}
                    </div>
                );
            })}
        </>
    );
});
DestinationCellContent.displayName = 'DestinationCellContent';

const HealthLegend = ({ t }: { t: (key: string) => string }) => (
    <div className="text-xs text-left">
        <p className="font-semibold mb-1 text-off-black">{t("Connection Health")}</p>
        <p className="mb-1.5 text-secondary-gray">{t("Connections are analyzed every hour.")}</p>
        {(["healthy", "unhealthy", "disabled"] as const).map((status) => {
            const s = getConnectionStatus(status, false, t);
            return (
                <div key={status} className="flex items-center gap-1.5 mb-1">
                    <span className="flex-shrink-0">{s.icon}</span>
                    <span className="text-secondary-gray">{s.text}</span>
                </div>
            );
        })}
    </div>
);

type ConnectionsProps = {
    canAddConnection: boolean,
    connections: Connection[],
    reloadConnections: boolean,
    organizations: Organization[],
    destinationUrls: string[],
    providerTypes: ProviderType[],
    loadingProviderTypes: boolean,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    onSelectConnection: Function,
    onChangeGlobalSearch: (value: string) => void,
    onChangeStatusFilter: Function,
    statusFilterValue: any,
    onChangeDestinationUrl: Function,
    destinationFilterValue: any,
    loadingDestinationUrls: boolean,
    onChangeOwner: Function,
    ownerFilterValue: any,
    loadingOrganizations: boolean,
    onChangeProviderType: Function,
    providerTypeFilterValue: any,
    onChangeDestinationType: Function,
    destinationTypeFilterValue: any,
    globalSearchStr: string,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number,
    onClickAdd: MouseEventHandler
}

const ConnectionsList = ({
    canAddConnection,
    connections,
    reloadConnections,
    organizations,
    destinationUrls,
    providerTypes,
    loadingProviderTypes,
    destinationTypes,
    loadingDestinationTypes,
    onSelectConnection,
    onChangeGlobalSearch,
    onChangeStatusFilter,
    statusFilterValue,
    onChangeDestinationUrl,
    destinationFilterValue,
    loadingDestinationUrls,
    onChangeOwner,
    ownerFilterValue,
    loadingOrganizations,
    onChangeProviderType,
    providerTypeFilterValue,
    onChangeDestinationType,
    destinationTypeFilterValue,
    globalSearchStr,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset,
    onClickAdd
}: ConnectionsProps) => {
    const { t } = useTranslation();

    const hasActiveFilters = !!(globalSearchStr || statusFilterValue || destinationFilterValue || ownerFilterValue || providerTypeFilterValue || destinationTypeFilterValue);

    const clearAllFilters = () => {
        onChangeGlobalSearch('');
        onChangeStatusFilter(null);
        onChangeDestinationUrl(null);
        onChangeOwner(null);
        onChangeProviderType(null);
        onChangeDestinationType(null);
    };

    const connectionHealthStatuses = useMemo(() => getConnectionHealthStatuses(t), [t]);
    const destinationUrlOptions = useMemo(() => destinationUrls.map(url => {
        let hostname = url;
        try { hostname = new URL(url).hostname; } catch {}
        return { label: hostname, value: url };
    }), [destinationUrls]);

    return (
        <div className={containerClasses.page}>
            <div className="mb-12 flex justify-between max-w-list-table mx-auto">
                <div>
                    <h1 data-testid="connections_page_header" className={textClasses.heading}>
                        {t("Connections")}
                    </h1>
                </div>
                {canAddConnection && <button className={buttonClasses.primary}
                    onClick={onClickAdd}
                >
                    {t("Create Connection")}
                </button>}
            </div>
            <div className="max-w-list-table mx-auto">
            {/* Search row */}
            <div className="flex flex-row items-end gap-3 mb-3">
                <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} placeholder={t("Search")} showClearButton={false} clearOnSearch={true} />
                {globalSearchStr && <SearchChip value={globalSearchStr} onClear={() => onChangeGlobalSearch('')} />}
                {hasActiveFilters && (
                    <button className={`${buttonClasses.lightGreen} whitespace-nowrap h-[38px] mb-2 ml-auto`} onClick={clearAllFilters}>
                        {t("Clear All Filters")}
                    </button>
                )}
            </div>
            {/* Filter dropdowns row */}
            <div className="flex flex-row gap-3 mb-3 flex-nowrap overflow-y-visible">
                {/* Provider Type */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={`${filterClasses.label} capitalize`}>{t("source")}</span>
                    <SelectInput
                        name="provider_type__in"
                        options={providerTypes.map(p => ({ label: p.name, value: p.value }))}
                        value={providerTypeFilterValue}
                        onChange={onChangeProviderType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingProviderTypes}
                        className=""
                        variant="connectionFilter"
                    />
                </div>
                {/* Destination Type */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Destination")}</span>
                    <SelectInput
                        name="destination_type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.value }))}
                        value={destinationTypeFilterValue}
                        onChange={onChangeDestinationType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className=""
                        variant="connectionFilter"
                    />
                </div>
                {/* Destination */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Destination URL")}</span>
                    <SelectInput
                        name="destination_url__in"
                        options={destinationUrlOptions}
                        value={destinationFilterValue}
                        onChange={onChangeDestinationUrl}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className=""
                        variant="connectionFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className=""
                        variant="connectionFilter"
                    />
                </div>
                {/* Health */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Health")}</span>
                    <SelectInput
                        name="status"
                        options={connectionHealthStatuses.map(s => ({ label: s.name, value: s.value }))}
                        value={statusFilterValue}
                        onChange={onChangeStatusFilter}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={false}
                        isDisabled={false}
                        isLoading={false}
                        className=""
                        variant="connectionFilter"
                    />
                </div>
            </div>

            {/* Cards */}
            <div className="flex flex-col w-full gap-2">
                {(!pageLoaded || reloadConnections) ? (
                    [...Array(8)].map((_, i) => (
                        <div key={i} className="bg-white border border-gray-lines rounded-lg p-4" data-testid="loading">
                            {/* Title row: name + health icon */}
                            <div className="flex items-center gap-2 mb-3">
                                <div className={`h-5 w-48 ${skeletonClasses.base}`} />
                                <div className={`h-5 w-5 ${skeletonClasses.base} rounded-full flex-shrink-0`} />
                            </div>
                            {/* Content row */}
                            <div className="flex items-start gap-4">
                                {/* Source */}
                                <div className="flex flex-col gap-1 w-40 flex-shrink-0">
                                    <div className={`h-4 w-28 ${skeletonClasses.base}`} />
                                    <div className={`h-3 w-24 ${skeletonClasses.base}`} />
                                </div>
                                {/* Arrow */}
                                <div className={`h-5 w-5 ${skeletonClasses.base} flex-shrink-0 mt-0.5`} />
                                {/* Destination */}
                                <div className="flex flex-col gap-1 w-64">
                                    <div className={`h-4 w-24 ${skeletonClasses.base}`} />
                                    <div className={`h-3 w-32 ${skeletonClasses.base}`} />
                                </div>
                                {/* Workspace */}
                                <div className={`h-4 w-28 ${skeletonClasses.base} flex-shrink-0`} />
                            </div>
                        </div>
                    ))
                ) : connections.length === 0 ? (
                    <NoResults
                        headerMsg={t("No Results Found")}
                        labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                    />
                ) : (
                    connections.map((connection: Connection) => {
                        const connectionStatus = getConnectionStatus(connection.status, false, t);
                        const navigateToConnection = (e: React.MouseEvent) => {
                            if (e.metaKey || e.ctrlKey) {
                                window.open(`/connections/${connection.id}`, '_blank', 'noopener,noreferrer');
                            } else {
                                onSelectConnection(connection.id);
                            }
                        };
                        return (
                            <div
                                key={connection.id}
                                className="bg-white border border-gray-lines rounded-lg p-4 cursor-pointer"
                                role="button"
                                tabIndex={0}
                                onClick={navigateToConnection}
                                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectConnection(connection.id); } }}
                            >
                                {/* Title row: name + health */}
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-connection-green text-[18px] font-medium truncate" title={connection.provider.name}>
                                        {connection.provider.name}
                                    </span>
                                    <HoverTooltip
                                        position="right"
                                        align="left"
                                        width="w-52"
                                        className="flex-shrink-0"
                                        ariaLabel={connectionStatus.text}
                                        content={<HealthLegend t={t} />}
                                    >
                                        {connectionStatus.icon}
                                    </HoverTooltip>
                                </div>
                                {/* Content row: source → destination + workspace */}
                                <div className="flex items-start gap-4">
                                    {/* Source */}
                                    <div className="flex flex-col min-w-0 w-40 flex-shrink-0">
                                        <span className="text-base font-normal text-grey-g0">{connection.provider.type.name}</span>
                                        {connection.provider.base_url && (
                                            <span className="text-xs text-secondary-gray truncate" title={connection.provider.base_url}>
                                                {getHostname(connection.provider.base_url)}
                                            </span>
                                        )}
                                    </div>
                                    {/* Arrow */}
                                    <div className="flex-shrink-0 mt-0.5">
                                        <ArrowForward size={20} className="text-secondary-gray" />
                                    </div>
                                    {/* Destination */}
                                    <div className="w-[280px] min-w-0 overflow-hidden">
                                        {connection.destinations.length === 0 ? (
                                            <span className="text-sm text-secondary-gray">{t("Destination not set")}</span>
                                        ) : (
                                            <DestinationCellContent destinations={connection.destinations} t={t} />
                                        )}
                                    </div>
                                    {/* Workspace */}
                                    <div className="flex-1 min-w-0 overflow-hidden">
                                        <span className="text-sm text-secondary-gray truncate block" title={connection.owner.name}>{connection.owner.name}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                {totalCount > 0 && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    totalCount={totalCount}
                    initialPage={Math.floor(offset / take)}
                    take={take}
                    loading={!pageLoaded}
                    mainclassname={paginationClasses.container}
                />}
            </div>
            </div>
        </div>
    )
};

export default ConnectionsList;
